import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getUserRepositories,
  getWorkspaces,
  getRepositoryBranches,
} from "../api/repositoryApi";

// Query keys
export const queryKeys = {
  workspaces: "workspaces",
  repositories: (workspace) => ["repositories", workspace],
  branches: (workspace, repoSlug) => ["branches", workspace, repoSlug],
};

/**
 * Hook to fetch and cache workspaces
 */
export const useWorkspaceQuery = (options = {}) => {
  return useQuery({
    queryKey: [queryKeys.workspaces],
    queryFn: () => getWorkspaces(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    ...options,
  });
};

/**
 * Hook to fetch and cache repositories for a workspace
 */
export const useRepositoriesQuery = (workspace, options = {}) => {
  return useQuery({
    queryKey: queryKeys.repositories(workspace),
    queryFn: () => getUserRepositories(workspace),
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    enabled: !!workspace, // Only run if workspace is provided
    ...options,
  });
};

/**
 * Hook to fetch and cache branches for a repository
 */
export const useBranchesQuery = (workspace, repoSlug, options = {}) => {
  return useQuery({
    queryKey: queryKeys.branches(workspace, repoSlug),
    queryFn: () => getRepositoryBranches(workspace, repoSlug),
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    enabled: !!(workspace && repoSlug), // Only run if both params are provided
    ...options,
  });
};

/**
 * Hook to manually invalidate repository data
 */
export const useRepositoryCache = () => {
  const queryClient = useQueryClient();

  return {
    invalidateWorkspaces: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeys.workspaces] }),

    invalidateRepositories: (workspace) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.repositories(workspace),
      }),

    invalidateBranches: (workspace, repoSlug) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.branches(workspace, repoSlug),
      }),

    // Invalidate everything related to repositories
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.workspaces] });
      queryClient.invalidateQueries({ queryKey: ["repositories"] });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
    },

    // Prefetch data (useful before navigation)
    prefetchRepositories: (workspace) =>
      queryClient.prefetchQuery({
        queryKey: queryKeys.repositories(workspace),
        queryFn: () => getUserRepositories(workspace),
      }),

    prefetchBranches: (workspace, repoSlug) =>
      queryClient.prefetchQuery({
        queryKey: queryKeys.branches(workspace, repoSlug),
        queryFn: () => getRepositoryBranches(workspace, repoSlug),
      }),
  };
};
